/* PDP Bundle Add-Ons
 * --------------------
 * Renders alongside the existing PDP add-ons snippet but with a different
 * UX: one card per VARIANT of a single cross-sell target product, each
 * card carrying its own quantity stepper. Hits a configurable qty
 * threshold to surface a tier discount that's applied at checkout via
 * Shopify native automatic Volume Discount.
 *
 * Integrates with the existing PDP add-to-cart flow by:
 *   (a) Patching `qvUpdateATCbtn` so the ATC button text counts bundle
 *       add-on qty in addition to the main qty + standard add-on qty.
 *   (b) Wrapping `pdpAddToCart` to append bundle items to the cart-add
 *       payload before it reaches `pdpExecuteAdd`.
 *
 * Stays a no-op on PDPs that don't render a `.pdp-bundle-addons` block.
 */

(function () {
  const root = document.querySelector('.pdp-bundle-addons');
  if (!root) return;

  /* Tier rules — sorted ascending by qty so the "next tier" lookup is
   * a linear walk. Each entry: { qty, pct }. Must match the shop
   * metafield haworth_bundles/configs entry for the same collection. */
  let tierRules = [];
  try {
    const raw = JSON.parse(root.dataset.tierRules || '[]');
    tierRules = raw
      .filter((t) => Number.isFinite(t.qty) && t.qty > 0)
      .sort((a, b) => a.qty - b.qty);
  } catch (e) {
    console.error('[bundle-addons] bad tier-rules JSON', e);
  }
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';

  /* Deepest sale markdown across bundle products, from Liquid (e.g.
   * 0.85 = 15% off compare-at). Combined with a tier percent this
   * yields the TOTAL savings vs compare-at for nudge copy — same
   * multiply-then-floor math as the promo pill, so all claims agree.
   * Defaults to 1 (no sale) → combinedPct(tier) === tier. */
  const minPriceRatio = parseFloat(root.dataset.minPriceRatio) || 1;
  const combinedPct = (tierPct) =>
    Math.floor(100 - minPriceRatio * (100 - tierPct));
  const cards = Array.from(root.querySelectorAll('.bundle-addon-card'));
  const tierHint = root.querySelector('[data-tier-hint]');
  const bundleTotal = root.querySelector('[data-bundle-total]');
  const bundleTotalAmount = root.querySelector('[data-bundle-total-amount]');
  const bundleTotalPre = root.querySelector('[data-bundle-total-pre]');

  /* Card-level interactions ------------------------------------------- */
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('.bundle-addon-card__qty-btn');
    if (!btn) return;
    const card = btn.closest('.bundle-addon-card');
    const input = card && card.querySelector('.bundle-addon-card__qty-input');
    if (!input || input.disabled) return;
    const step = parseInt(btn.dataset.qtyStep, 10) || 0;
    const min = parseInt(input.dataset.min, 10) || 0;
    const max = parseInt(input.dataset.max, 10) || 99;
    const cur = parseInt(input.value, 10) || 0;
    const next = Math.max(min, Math.min(max, cur + step));
    if (next === cur) return;
    input.value = String(next);
    recompute();
  });

  root.addEventListener('input', (e) => {
    if (!e.target.classList.contains('bundle-addon-card__qty-input')) return;
    const min = parseInt(e.target.dataset.min, 10) || 0;
    const max = parseInt(e.target.dataset.max, 10) || 99;
    let v = parseInt(e.target.value, 10);
    if (isNaN(v) || v < min) v = min;
    if (v > max) v = max;
    if (String(v) !== e.target.value) e.target.value = String(v);
    recompute();
  });

  /* Recompute totals + tier state ------------------------------------- */
  function getSelections() {
    return cards
      .map((card) => {
        const qty = parseInt(card.querySelector('.bundle-addon-card__qty-input').value, 10) || 0;
        const variantId = parseInt(card.dataset.variantId, 10);
        const unitPrice = parseInt(card.dataset.unitPrice, 10) || 0;
        card.classList.toggle('is-active', qty > 0);
        return { card, variantId, unitPrice, qty };
      })
      .filter((s) => s.qty > 0);
  }

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    const withComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','));
  }

  function getMainQty() {
    const pdpInput = document.getElementById('pdp-quantity');
    return pdpInput ? parseInt(pdpInput.value, 10) || 0 : 0;
  }

  function getMainUnitPrice() {
    /* pdpSelectedVariant is assigned without a declaration in
     * template-product.js (line ~75), so it's an implicit global. Bare-
     * word lookup finds it; `window.pdpSelectedVariant` returns undefined
     * in iframe sandbox contexts. */
    try {
      if (typeof pdpSelectedVariant !== 'undefined' && pdpSelectedVariant) {
        return parseInt(pdpSelectedVariant.price, 10) || 0;
      }
    } catch (e) {}
    return 0;
  }

  /* Tier resolver — given a total qty, returns
   *   { current: TierRule|null, next: TierRule|null }
   * `current` is the highest tier whose qty <= grandQty (or null below
   * the lowest tier). `next` is the very next tier above current (or
   * null if already at the top). */
  function resolveTiers(grandQty) {
    let current = null;
    let next = null;
    for (const t of tierRules) {
      if (grandQty >= t.qty) {
        current = t;
      } else {
        next = t;
        break;
      }
    }
    return { current, next };
  }

  function recompute() {
    const selections = getSelections();
    const bundleQty = selections.reduce((sum, s) => sum + s.qty, 0);
    const bundleSubtotalCents = selections.reduce((sum, s) => sum + s.qty * s.unitPrice, 0);
    const mainQty = getMainQty();
    const mainUnit = getMainUnitPrice();
    const grandQty = bundleQty + mainQty;
    const grandSubtotalCents = bundleSubtotalCents + mainQty * mainUnit;

    const { current, next } = resolveTiers(grandQty);
    const meetsTier = !!current;
    const discountPct = meetsTier ? current.pct : 0;
    const discountCents = meetsTier ? Math.round((grandSubtotalCents * discountPct) / 100) : 0;
    const finalCents = grandSubtotalCents - discountCents;

    if (tierHint) {
      /* Per Jessica's Figma, the tier hint is ALWAYS green — the copy
       * itself communicates progress ("you're saving X" vs "add N more"),
       * the color says "this is the savings story." No gray/green
       * toggle, no `.is-met` class needed. */
      if (meetsTier && next) {
        /* Hit a tier AND there's a higher tier to chase. */
        const remaining = next.qty - grandQty;
        const itemsWord = remaining === 1 ? '1 more' : `${remaining} more`;
        tierHint.textContent =
          `${current.pct}% off applied at checkout — saving ${formatMoney(discountCents)}. Add ${itemsWord} for ${combinedPct(next.pct)}% off.`;
      } else if (meetsTier) {
        /* Hit the top tier — nothing more to chase. */
        tierHint.textContent =
          `${current.pct}% off applied at checkout — you’re saving ${formatMoney(discountCents)}.`;
      } else if (next) {
        /* Below the lowest tier — show entry-tier nudge. */
        const remaining = next.qty - grandQty;
        const itemsWord = remaining === 1 ? '1 more' : `${remaining} more`;
        tierHint.textContent = `Add ${itemsWord} to save ${combinedPct(next.pct)}%.`;
      } else {
        /* No tier rules configured. */
        tierHint.textContent = '';
      }
    }

    if (bundleTotal && bundleTotalAmount) {
      if (bundleQty > 0) {
        bundleTotal.hidden = false;
        /* The tier-hint line above carries the percent + "applied at
         * checkout" context — bundle total just shows the math. When
         * a tier is hit, strike through the pre-discount total so the
         * savings are visually obvious next to the final number. */
        bundleTotalAmount.textContent = formatMoney(finalCents);
        if (bundleTotalPre) {
          if (meetsTier) {
            bundleTotalPre.hidden = false;
            bundleTotalPre.textContent = formatMoney(grandSubtotalCents);
          } else {
            bundleTotalPre.hidden = true;
          }
        }
      } else {
        bundleTotal.hidden = true;
      }
    }

    /* Mirror standard add-on behavior — let the main PDP code recount
     * the ATC button label. We bump it manually too, since
     * qvUpdateATCbtn only walks standard add-on checkboxes. */
    if (typeof window.qvUpdateATCbtn === 'function') {
      window.qvUpdateATCbtn();
    }
    updateATCBtnLabel(grandQty);
  }

  /* Bundle-aware ATC button label ------------------------------------- */
  function updateATCBtnLabel(grandQty) {
    /* Recount standard add-on qty too so we don't undercount. */
    let standardAddOnQty = 0;
    document.querySelectorAll('.addon-card__checkbox:checked').forEach((el) => {
      const card = el.closest('.addon-card');
      if (card) standardAddOnQty += parseInt(card.dataset.qty, 10) || 1;
    });
    const total = grandQty + standardAddOnQty;
    const pdpAddBtns = document.querySelectorAll('.pdp .pdp-form__submit');
    pdpAddBtns.forEach((btn) => {
      if (total > 1) {
        /* Title Case per final Figma (Option 1): "Add 3 Items to Cart" */
        btn.textContent = `Add ${total} Items to Cart`;
      } else if (!btn.disabled) {
        btn.textContent = btn.dataset.atc || 'Add to cart';
      }
    });
  }

  /* React to main-PDP qty changes & variant changes ------------------- */
  const mainQtyInput = document.getElementById('pdp-quantity');
  if (mainQtyInput) {
    mainQtyInput.addEventListener('input', recompute);
    /* The increase/decrease helpers mutate the input but don't dispatch
     * 'input' — listen on click of those buttons too. */
    document
      .querySelectorAll('.QuantitySelector__Button')
      .forEach((b) => b.addEventListener('click', () => setTimeout(recompute, 0)));
  }
  /* When the main variant changes, template-product.js updates
   * `pdpSelectedVariant`. Listen via the existing change broadcast or
   * poll on a generic event hook — we rebind on any pdp-form change. */
  const pdpForm = document.querySelector('.pdp-form, form[action*="/cart/add"]');
  if (pdpForm) {
    pdpForm.addEventListener('change', () => setTimeout(recompute, 0));
  }

  /* Cart-add patch — append bundle items before pdpExecuteAdd --------- */
  if (typeof window.pdpAddToCart === 'function' && !window.__pdpAddToCartBundleWrapped) {
    const originalAddToCart = window.pdpAddToCart;
    window.__pdpAddToCartBundleWrapped = true;
    window.pdpAddToCart = function (e) {
      const selections = getSelections();
      if (selections.length === 0) {
        return originalAddToCart(e);
      }
      /* Patch pdpExecuteAdd just for this invocation so we can splice in
       * bundle items right before the /cart/add.js call. Restore
       * immediately after. */
      const realExecute = window.pdpExecuteAdd;
      window.pdpExecuteAdd = function (addObj, thisBtn, addCallback) {
        try {
          selections.forEach((s) => {
            addObj.items.push({ id: s.variantId, quantity: s.qty });
          });
        } catch (err) {
          console.error('[bundle-addons] failed to append bundle items', err);
        }
        window.pdpExecuteAdd = realExecute;
        return realExecute(addObj, thisBtn, addCallback);
      };
      return originalAddToCart(e);
    };
  }

  /* Initial render */
  recompute();
})();
